import {
	faActors,
	faBook,
	faExclamationTriangle,
	faMagnifyingGlass,
	faNextjs,
	faQuestionSquare,
	faReact,
	faSidebar,
	faSidebarFlip,
	faTs,
	Icon,
} from "@rivet-gg/icons";
import {
	useInfiniteQuery,
	useQuery,
	useSuspenseInfiniteQuery,
} from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Suspense, useCallback, useEffect, useRef, type RefObject } from "react";
import { useLocalStorage } from "usehooks-ts";
import { RECORDS_PER_PAGE } from "@/app/data-providers/default-data-provider";
import {
	Button,
	FilterCreator,
	FiltersDisplay,
	H4,
	ls,
	type OnFiltersChange,
	ScrollArea,
	ShimmerLine,
	SmallText,
	WithTooltip,
} from "@/components";
import { docsLinks } from "@/content/data";
import { features } from "@/lib/features";
import { VisibilitySensor } from "../visibility-sensor";
import { useActorsFilters, useFiltersValue } from "./actor-filters-context";
import { useActorsLayout } from "./actors-layout-context";
import { ActorsListRow, ActorsListRowSkeleton } from "./actors-list-row";
import { useActorsView } from "./actors-view-context-provider";
import { CreateActorButton } from "./create-actor-button";
import { useDataProvider } from "./data-provider";
import { GoToActorButton } from "./go-to-actor-button";
import { NoProvidersAlert } from "./no-providers-alert";
import { useRootLayout } from "./root-layout-context";

export function ActorsList() {
	const viewportRef = useRef<HTMLDivElement>(null);

	return (
		<ScrollArea
			className="h-full w-full @container/main"
			viewportRef={viewportRef}
		>
			<TopBar />
			<Page1Poller />
			<Suspense fallback={<ListSkeleton />}>
				<List viewportRef={viewportRef} />
				<Pagination />
			</Suspense>
		</ScrollArea>
	);
}

function Page1Poller() {
	const { n } = useSearch({ from: "/_context" });
	const filters = useFiltersValue({ onlyStatic: true });
	useQuery(useDataProvider().actorsListPage1PollQueryOptions({ n, filters }));
	return null;
}

function TopBar() {
	const { isSidebarCollapsed, sidebarRef } = useRootLayout();
	const { isDetailsColCollapsed, detailsRef } = useActorsLayout();

	return (
		<div className="col-span-full border-b flex px-2 py-2 gap-1 @lg/h-[45px] sticky top-0 bg-card z-[1]">
			{isSidebarCollapsed ? (
				<WithTooltip
					trigger={
						<Button
							onClick={() => sidebarRef.current?.expand()}
							variant="outline"
							size="icon-sm"
						>
							<Icon icon={faSidebar} />
						</Button>
					}
					content="Expand Actor Names column"
				/>
			) : null}
			<div className="justify-between flex flex-1 flex-wrap gap-2 w-full">
				{features.datacenter ? (
					<Filters />
				) : (
					<div />
				)}
				<div className="flex gap-1">
					<CreateActorButton />
					<Display />
				</div>
			</div>
			{isDetailsColCollapsed ? (
				<WithTooltip
					trigger={
						<Button
							onClick={() => detailsRef.current?.expand()}
							variant="outline"
							size="icon-sm"
						>
							<Icon icon={faSidebarFlip} />
						</Button>
					}
					content="Expand details column"
				/>
			) : null}
			<LoadingIndicator />
		</div>
	);
}

function LoadingIndicator() {
	const n = useSearch({
		from: "/_context",
		select: (state) => state.n,
	});

	const filters = useFiltersValue({ onlyStatic: true });
	const { isLoading } = useInfiniteQuery(
		useDataProvider().actorsListQueryOptions({ n, filters }),
	);
	if (isLoading) {
		return <ShimmerLine className="bottom-0" />;
	}
	return null;
}

function List({
	viewportRef,
}: { viewportRef: RefObject<HTMLDivElement | null> }) {
	const filters = useFiltersValue({ onlyStatic: true });
	const { actorId, n } = useSearch({
		from: "/_context",
	});
	const { data: actors = [] } = useInfiniteQuery(
		useDataProvider().actorsListQueryOptions({ n, filters }),
	);

	const rowVirtualizer = useVirtualizer({
		count: actors.length,
		getScrollElement: () => viewportRef.current,
		estimateSize: () => 56,
		overscan: 5,
	});

	useEffect(() => {
		rowVirtualizer.measure();
	}, [rowVirtualizer]);

	return (
		<div
			className="relative w-full"
			style={{ height: rowVirtualizer.getTotalSize() }}
		>
			{rowVirtualizer.getVirtualItems().map((virtualItem) => {
				const actor = actors[virtualItem.index];
				return (
					<div
						key={actor.actorId}
						ref={rowVirtualizer.measureElement}
						data-index={virtualItem.index}
						className="absolute inset-x-0"
						style={{
							transform: `translateY(${virtualItem.start}px)`,
						}}
					>
						<ActorsListRow
							actorId={actor.actorId}
							isCurrent={actorId === actor.actorId}
						/>
					</div>
				);
			})}
		</div>
	);
}

function Pagination() {
	const n = useSearch({
		from: "/_context",
		select: (state) => state.n,
	});
	const filters = useFiltersValue({ onlyStatic: true });
	const { hasNextPage, isFetchingNextPage, fetchNextPage, data } =
		useSuspenseInfiniteQuery(
			useDataProvider().actorsListPaginationQueryOptions({
				n,
				filters,
			}),
		);

	if (isFetchingNextPage) {
		return <ListSkeleton />;
	}

	if (hasNextPage) {
		return <VisibilitySensor onChange={fetchNextPage} />;
	}

	return <EmptyState count={data || 0} />;
}

export function ListSkeleton() {
	return (
		<div className="w-full">
			{Array(RECORDS_PER_PAGE)
				.fill(null)
				.map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeleton loaders are static
					<ActorsListRowSkeleton key={i} />
				))}
		</div>
	);
}

const useRunnerConfigs = () => {
	const dataProvider = useDataProvider();
	const { data: runnerNamesCount = 0 } = useInfiniteQuery({
		...dataProvider.runnerNamesQueryOptions(),
		select: (data) => data.pages.flatMap((page) => page.names).length,
	});

	const { data: runnerConfigsCount = 0 } = useInfiniteQuery({
		...dataProvider.runnerConfigsQueryOptions(),
		select: (data) =>
			data.pages.flatMap((page) => Object.keys(page.runnerConfigs))
				.length,
	});

	return runnerConfigsCount + runnerNamesCount;
};

function EmptyState({ count }: { count: number }) {
	const navigate = useNavigate();
	const names = useSearch({
		from: "/_context",
		select: (state) => state.n,
	});
	const { copy } = useActorsView();
	const { remove, pick } = useActorsFilters();

	const dataProvider = useDataProvider();

	const { data: availableNamesCount = 0 } = useInfiniteQuery(
		dataProvider.buildsCountQueryOptions(),
	);

	const runnerConfigsCount = useRunnerConfigs();

	const filtersCount = useSearch({
		from: "/_context",
		select: (state) =>
			Object.values(pick(state, { onlyStatic: true })).length,
	});

	const clearFilters = () => {
		return navigate({
			to: ".",
			search: (prev) => ({
				...remove(prev || {}),
			}),
		});
	};

	return (
		<div className=" col-span-full my-4 flex flex-col items-center gap-2 justify-center">
			{(!names || names?.length === 0) && availableNamesCount > 0 ? (
				<div className="flex text-center text-foreground flex-1 justify-center items-center flex-col gap-2 my-12">
					<Icon icon={faQuestionSquare} className="text-4xl" />
					<p className="max-w-[400px]">
						No Actor Name selected.
						<br />
						<span className="text-sm text-muted-foreground">
							Select an Actor Name from the list on the left.
						</span>
					</p>
				</div>
			) : count === 0 ? (
				filtersCount === 0 ? (
					runnerConfigsCount === 0 ? (
						<div className="px-4">
							<NoProvidersAlert />
						</div>
					) : (
						<div className="gap-2 flex flex-col items-center justify-center">
							<Icon icon={faActors} className="text-4xl mt-8" />
							<SmallText className="text-center my-0">
								{copy.noActorsFound}
							</SmallText>
							<div className="mt-4 flex flex-col gap-2 items-center justify-center">
								<CreateActorButton variant="secondary" />{" "}
								<SmallText className="mt-4 mb-1">
									Use one of the quick start guides to get
									started.
								</SmallText>
								<div className="flex gap-2">
									<Button
										className="flex-1"
										variant="outline"
										startIcon={<Icon icon={faTs} />}
										asChild
									>
										<a
											href={docsLinks.gettingStarted.js}
											target="_blank"
											rel="noopener noreferrer"
										>
											JavaScript
										</a>
									</Button>
									<Button
										className="flex-1"
										variant="outline"
										startIcon={<Icon icon={faReact} />}
										asChild
									>
										<a
											href={
												docsLinks.gettingStarted.react
											}
											target="_blank"
											rel="noopener noreferrer"
										>
											React
										</a>
									</Button>

									<Button
										className="flex-1"
										variant="outline"
										startIcon={<Icon icon={faNextjs} />}
										asChild
									>
										<a
											href={
												docsLinks.gettingStarted.nextjs
											}
											target="_blank"
											rel="noopener noreferrer"
										>
											Next.js
										</a>
									</Button>
								</div>
							</div>
						</div>
					)
				) : (
					<>
						<SmallText className="text-foreground text-center mt-8 mb-2">
							{copy.noActorsMatchFilter}
						</SmallText>
						<Button variant="outline" mx="4" onClick={clearFilters}>
							Clear filter
						</Button>
					</>
				)
			) : (
				<SmallText className="text-muted-foreground text-center text-xs">
					{copy.noMoreActors}
				</SmallText>
			)}
		</div>
	);
}

function useFiltersChangeCallback(): OnFiltersChange {
	const navigate = useNavigate();
	const { pick, remove } = useActorsFilters();
	const [value, setLs] = useLocalStorage(
		ls.actorsEphemeralFilters.key,
		() => ({ wakeOnSelect: { value: ["1"] } }),
		{
			deserializer: (value) => JSON.parse(value),
			serializer: (value) => JSON.stringify(value),
		},
	);

	return useCallback(
		(fnOrValue) => {
			if (typeof fnOrValue === "function") {
				navigate({
					to: ".",
					search: (old) => {
						const filters = pick(old || {}, { onlyStatic: true });
						const prev = remove(old || {});

						return {
							...prev,
							...Object.fromEntries(
								Object.entries(
									pick(fnOrValue(filters), {
										onlyStatic: true,
									}),
								).filter(
									([, filter]) => filter.value.length > 0,
								),
							),
						};
					},
				});

				setLs(pick(fnOrValue(value), { onlyEphemeral: true }));
			} else {
				navigate({
					to: ".",
					search: (value) => ({
						...remove(value || {}),
						...Object.fromEntries(
							Object.entries(
								pick(fnOrValue, { onlyStatic: true }),
							).filter(([, filter]) => filter.value.length > 0),
						),
					}),
				});
				setLs(pick(fnOrValue || {}, { onlyEphemeral: true }));
			}
		},
		[navigate, pick, remove, setLs, value],
	);
}

function Filters() {
	return <GoToActorButton />;
}

function Display() {
	const { definitions } = useActorsFilters();
	const filters = useFiltersValue();
	const onFiltersChange = useFiltersChangeCallback();

	return (
		<>
			<FiltersDisplay
				value={filters}
				definitions={definitions}
				onChange={onFiltersChange}
			/>
		</>
	);
}
